/**
 * Zod schemas for the admin candidate/outreach pipeline endpoints consumed
 * by `@/components/admin/CandidateOutreachPipeline` (GitHub issue #498 —
 * "E2E: Sourcing & outreach — pipeline tracking, email management,
 * replies").
 *
 * Follows the same convention as `./admin.ts` (hand-written Zod mirrors of
 * `@herohire/types`, `.parse()`d at the query boundary).
 *
 * TODO(backend): none of these endpoints exist yet in
 * `apps/workers/src/api/admin/**` or `functions/api/**` — `JobDetail.tsx`
 * (pre-this-issue) already documented that `GET
 * /api/admin/jobs/:jobId/candidates` / `GET /api/admin/jobs/:jobId/outreach`
 * don't exist, and this issue's pipeline/thread/bulk/reply/auto-followup
 * surface needs a combined, richer shape those two endpoints didn't even
 * originally cover (replies, auto-followup opt-out, bulk mutations).
 * `useAdminJobPipeline`/the mutation hooks in `@/lib/api/apiClient.ts`
 * call the paths documented per-schema below; until a backend implements
 * them, every mutation degrades gracefully by applying its optimistic
 * local-state update regardless of the network result (see each mutation
 * hook's `onError` — this keeps the UI/E2E-testable via `page.route()`
 * mocks without the app being fully unusable against a real, un-migrated
 * backend). Adding the real endpoints is a backend-only follow-up.
 */

import { z } from 'zod';

// ========================================================================
// Shared enums
// ========================================================================

/** Outreach message lifecycle status — mirrors `AdminOutreachMessage['status']`
 * (`@herohire/types`), which already covers the tabs this ticket asks for
 * (Queued/Sent/Replied/etc.). */
export const outreachMessageStatusSchema = z.enum([
  'DRAFT',
  'QUEUED',
  'SENT',
  'BOUNCED',
  'OPENED',
  'REPLIED',
  'FAILED',
]);
export type OutreachMessageStatus = z.infer<typeof outreachMessageStatusSchema>;

/** A single message in a candidate's outreach thread — either the
 * original outbound message, a candidate reply, or an admin-composed
 * follow-up/reply. */
export const outreachThreadMessageSchema = z.object({
  id: z.string(),
  direction: z.enum(['OUTBOUND', 'INBOUND']),
  author: z.enum(['SYSTEM', 'ADMIN', 'CANDIDATE']),
  subject: z.string().nullable().optional(),
  body: z.string(),
  aiGenerated: z.boolean().default(false),
  createdAt: z.string(),
});
export type OutreachThreadMessage = z.infer<typeof outreachThreadMessageSchema>;

/** One row in the candidate pipeline: the candidate, their current
 * outreach message, and the full thread (original + replies). */
export const pipelineCandidateSchema = z.object({
  candidate: z.object({
    id: z.string(),
    uuid: z.string(),
    name: z.string(),
    email: z.string().nullable().optional(),
    title: z.string().nullable().optional(),
    company: z.string().nullable().optional(),
    fit_score: z.number().nullable().optional(),
  }),
  message: z.object({
    id: z.string(),
    uuid: z.string(),
    subject: z.string(),
    body: z.string(),
    status: outreachMessageStatusSchema,
    sent_at: z.string().nullable().optional(),
    opened_at: z.string().nullable().optional(),
    replied_at: z.string().nullable().optional(),
    bounced_at: z.string().nullable().optional(),
  }),
  thread: z.array(outreachThreadMessageSchema),
  /** Per-candidate auto-followup opt-out (see 13a-outreach-pacing-engine.md
   * §4/§5 for the follow-up-sequence concept this toggle suppresses). */
  autoFollowupOptOut: z.boolean().default(false),
});
export type PipelineCandidate = z.infer<typeof pipelineCandidateSchema>;

// ========================================================================
// GET /api/admin/jobs/:jobId/pipeline
// ========================================================================

export const adminJobPipelineResponseSchema = z.object({
  jobId: z.string(),
  /** Job-level auto-followup sequence toggle. */
  autoFollowupEnabled: z.boolean().default(true),
  candidates: z.array(pipelineCandidateSchema),
});
export type AdminJobPipelineResponse = z.infer<typeof adminJobPipelineResponseSchema>;

// ========================================================================
// Mutation response schemas
// ========================================================================

/** `PATCH /api/admin/outreach/:messageId` (edit body) and
 * `POST /api/admin/outreach/:messageId/regenerate` (AI regenerate) both
 * return the updated message. */
export const outreachMessageUpdateResponseSchema = z.object({
  id: z.string(),
  subject: z.string(),
  body: z.string(),
  status: outreachMessageStatusSchema,
});
export type OutreachMessageUpdateResponse = z.infer<typeof outreachMessageUpdateResponseSchema>;

/** `POST /api/admin/outreach/:messageId/send` */
export const sendOutreachResponseSchema = z.object({
  id: z.string(),
  status: outreachMessageStatusSchema,
  sent_at: z.string(),
});
export type SendOutreachResponse = z.infer<typeof sendOutreachResponseSchema>;

/** `POST /api/admin/jobs/:jobId/outreach/send-all` */
export const sendAllOutreachResponseSchema = z.object({
  sentCount: z.number(),
  messageIds: z.array(z.string()),
  sent_at: z.string(),
});
export type SendAllOutreachResponse = z.infer<typeof sendAllOutreachResponseSchema>;

/** `POST /api/admin/jobs/:jobId/outreach/sync-replies` */
export const syncRepliesResponseSchema = z.object({
  newReplies: z.array(
    z.object({
      messageId: z.string(),
      reply: outreachThreadMessageSchema,
    })
  ),
});
export type SyncRepliesResponse = z.infer<typeof syncRepliesResponseSchema>;

/** `POST /api/admin/outreach/:messageId/replies` (compose + send) and
 * `POST /api/admin/outreach/:messageId/replies/generate` (AI draft, not
 * sent) both return a single thread message. The `generate` variant's
 * `id` is a client-disposable draft id, not a persisted message. */
export const replyResponseSchema = outreachThreadMessageSchema;
export type ReplyResponse = z.infer<typeof replyResponseSchema>;

/** `PATCH /api/admin/candidates/bulk-status` */
export const bulkStatusChangeResponseSchema = z.object({
  updatedCount: z.number(),
  candidateIds: z.array(z.string()),
  status: outreachMessageStatusSchema,
});
export type BulkStatusChangeResponse = z.infer<typeof bulkStatusChangeResponseSchema>;

/** `POST /api/admin/outreach/bulk-send` */
export const bulkSendResponseSchema = z.object({
  sentCount: z.number(),
  messageIds: z.array(z.string()),
});
export type BulkSendResponse = z.infer<typeof bulkSendResponseSchema>;

/** `POST /api/admin/outreach/bulk-regenerate` */
export const bulkRegenerateResponseSchema = z.object({
  regeneratedCount: z.number(),
  messages: z.array(outreachMessageUpdateResponseSchema),
});
export type BulkRegenerateResponse = z.infer<typeof bulkRegenerateResponseSchema>;
